import maplibregl from 'maplibre-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Layer } from '@deck.gl/core';
import type { MapAdapter } from '../types';

const TITILER = 'https://titiler-centinela-746476722093.us-central1.run.app';

/**
 * Wraps MapLibre GL JS as a MapAdapter.
 *
 * addLayer / removeLayer manage deck.gl Layer instances via a MapboxOverlay
 * (non-interleaved canvas overlay, synchronized to the MapLibre viewport).
 *
 * addRasterSource / removeRasterSource handle tile overlay layers
 * (GEE band tiles, titiler COG tiles) directly via the MapLibre map.
 *
 * addPlanetBasemap — adds the Planet monthly mosaic as a persistent basemap.
 * addSapiBoundary  — adds the Sapi boundary as a permanent vector overlay.
 */
export class MapLibreAdapter implements MapAdapter {
  readonly map: maplibregl.Map;
  private overlay: MapboxOverlay;
  private deckLayers: Map<string, Layer> = new Map();
  private overlayReady = false;

  constructor(containerId: string, center: [number, number], zoom: number) {
    this.map = new maplibregl.Map({
      container: containerId,
      style: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
      center: [center[1], center[0]],  // MapLibre: [lng, lat]
      zoom,
      interactive: true,
      attributionControl: false,
    });

    this.map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        customAttribution: '© OpenStreetMap · © CARTO · © Planet Labs',
      }),
      'bottom-left',
    );

    this.overlay = new MapboxOverlay({ layers: [] });

    this.map.on('load', () => {
      this.map.addControl(this.overlay);
      this.overlayReady = true;
      // Flush any layers that were added before the map finished loading
      if (this.deckLayers.size > 0) {
        this.overlay.setProps({ layers: [...this.deckLayers.values()] });
      }
    });
  }

  flyTo(center: [number, number], zoom: number, durationSec = 1.4): void {
    this.map.flyTo({
      center: [center[1], center[0]],
      zoom,
      duration: durationSec * 1000,
      essential: true,
    });
  }

  /** Add or replace a deck.gl Layer in the overlay (matched by layer.id). */
  addLayer<T>(layer: T): void {
    const l = layer as unknown as Layer;
    this.deckLayers.set(l.id, l);
    if (this.overlayReady) {
      this.overlay.setProps({ layers: [...this.deckLayers.values()] });
    }
  }

  /** Remove a deck.gl Layer from the overlay by id. */
  removeLayer<T>(layer: T): void {
    const l = layer as unknown as { id: string };
    this.deckLayers.delete(l.id);
    if (this.overlayReady) {
      this.overlay.setProps({ layers: [...this.deckLayers.values()] });
    }
  }

  /** Add a raster tile overlay (GEE tiles, titiler COG tiles, etc.). */
  addRasterSource(id: string, tileUrl: string, opacity = 0.72): void {
    const doAdd = () => {
      if (this.map.getSource(id)) this.removeRasterSource(id);
      this.map.addSource(id, { type: 'raster', tiles: [tileUrl], tileSize: 256 });
      this.map.addLayer({
        id,
        type: 'raster',
        source: id,
        paint: { 'raster-opacity': opacity },
      });
    };
    if (this.map.isStyleLoaded()) doAdd();
    else this.map.once('load', doAdd);
  }

  removeRasterSource(id: string): void {
    try {
      if (this.map.getLayer(id))  this.map.removeLayer(id);
      if (this.map.getSource(id)) this.map.removeSource(id);
    } catch { /* ignore if called before style loads */ }
  }

  /**
   * Add the Planet monthly mosaic as a persistent basemap.
   * Inserted below symbol layers so map labels stay legible.
   * Also updates the floating #sm-map-header text.
   */
  addPlanetBasemap(cogUri: string, monthKey: string): void {
    // Planet COGs are uint16 with nodata=0 in metadata — omit nodata=nan (causes 500 on integer types)
    const tileUrl =
      `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
      `?url=${encodeURIComponent(cogUri)}` +
      `&bidx=3&bidx=2&bidx=1` +
      `&rescale=500,2500&rescale=500,2500&rescale=500,2500`;

    const doAdd = () => {
      if (this.map.getSource('planet-basemap')) return;
      this.map.addSource('planet-basemap', {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: 256,
      });
      // Insert before the first symbol layer so labels render above Planet
      const firstSymbol = this.map.getStyle().layers?.find(l => l.type === 'symbol')?.id;
      this.map.addLayer(
        { id: 'planet-basemap', type: 'raster', source: 'planet-basemap',
          paint: { 'raster-opacity': 0.88 } },
        firstSymbol,
      );
    };
    if (this.map.isStyleLoaded()) doAdd(); else this.map.once('load', doAdd);

    // Update map header text
    const hdr = document.getElementById('sm-map-header');
    if (hdr) {
      const [year, month] = monthKey.split('-');
      const label = new Date(+year, +month - 1).toLocaleDateString('en', {
        month: 'long', year: 'numeric',
      });
      hdr.textContent = `Planet Monthly Mosaic · ${label}`;
    }
  }

  /**
   * Add the Sapi Safari Area boundary as a permanent dashed line overlay.
   * Both the glow and crisp line layers are never removed.
   */
  addSapiBoundary(data: object): void {
    const doAdd = () => {
      if (this.map.getSource('sapi-boundary')) return;
      this.map.addSource('sapi-boundary', { type: 'geojson', data: data as GeoJSON.FeatureCollection });
      // Soft glow behind the line
      this.map.addLayer({
        id: 'sapi-boundary-glow',
        type: 'line',
        source: 'sapi-boundary',
        paint: { 'line-color': '#97B8CB', 'line-width': 5, 'line-opacity': 0.15, 'line-blur': 6 },
      });
      // Crisp dashed line
      this.map.addLayer({
        id: 'sapi-boundary',
        type: 'line',
        source: 'sapi-boundary',
        paint: {
          'line-color': '#97B8CB',
          'line-width': 1.2,
          'line-opacity': 0.60,
          'line-dasharray': [4, 3],
        },
      });
    };
    if (this.map.isStyleLoaded()) doAdd(); else this.map.once('load', doAdd);
  }

  on(event: string, handler: () => void): void {
    this.map.on(event as maplibregl.MapEventType, handler);
  }
}
